import { Button, Cell, Grid, Heading, HFlow, Icon, Tooltip, useTheme } from 'bold-ui'
import { useAcessoLotacao } from 'components/auth/useAcessoLotacao'
import { Box } from 'components/Box'
import {
  CheckboxField,
  EquipeSelectField,
  EquipeSelectModel,
  Form,
  FormDebouncedValueSpy,
  FormRenderProps,
  SubmitButton,
  SwitchField,
} from 'components/form'
import DateRangeField from 'components/form/field/DateRangeField'
import { TipoServicoSelectField } from 'components/form/field/select/TipoServicoSelectField'
import createDecorator from 'final-form-calculate'
import { OrdemListaAtendimentoEnum, Period, StatusAtendimento, TipoServico } from 'graphql/types.generated'
import React, { CSSProperties, useMemo } from 'react'
import { metaPath } from 'util/metaPath'
import { LotacaoSelectField, LotacaoSelectFieldModel } from 'view/profissional/lotacao/LotacaoSelectField'

import { ListaAtendimentoDefaultFilter } from '../ListaAtendimentoView'
import { createStatusAtendimentoStyles } from '../useListaAtendimentoStyles'

export interface ListaAtendimentoFilterPopperModel {
  statusAtendimento?: StatusAtendimento[]
  periodo: Period
  tiposServico?: TipoServico[]
  lotacoes?: LotacaoSelectFieldModel[]
  equipes?: EquipeSelectModel[]
  ordem?: OrdemListaAtendimentoEnum
  somenteNaoFinalizados?: boolean
}

const path = metaPath<ListaAtendimentoFilterPopperModel>()

const filterFormDecorator = () =>
  createDecorator({
    field: path.somenteNaoFinalizados.absolutePath(),
    updates: {
      [path.statusAtendimento.absolutePath()]: (value: boolean, allValues: ListaAtendimentoFilterPopperModel) =>
        value
          ? allValues.statusAtendimento &&
            allValues.statusAtendimento.length > 0 &&
            allValues.statusAtendimento.filter(
              (item) => item !== StatusAtendimento.ATENDIMENTO_REALIZADO && item !== StatusAtendimento.NAO_AGUARDOU
            )
          : allValues.statusAtendimento,
      [path.periodo.absolutePath()]: (value: boolean) =>
        value ? { startDate: undefined, endDate: undefined } : ListaAtendimentoDefaultFilter.filter.periodo,
    },
  })

export interface ListaAtendimentoPopperProps {
  initialValues?: ListaAtendimentoFilterPopperModel
  isFilterEqual?: boolean
  onFechar(): void
  onSubmit(values: ListaAtendimentoFilterPopperModel): void
  onClear(): void
  onChangeFilter(values: ListaAtendimentoFilterPopperModel): void
}

export function ListaAtendimentoFilterPopperForm(props: ListaAtendimentoPopperProps) {
  const { initialValues, isFilterEqual, onSubmit, onFechar, onClear, onChangeFilter } = props
  const { unidadeSaude } = useAcessoLotacao()
  const theme = useTheme()
  const statusAtendStyles = createStatusAtendimentoStyles(theme)
  const decorator = useMemo(() => [filterFormDecorator()], [])

  const renderForm = (formProps: FormRenderProps<ListaAtendimentoFilterPopperModel>) => {
    const isSomenteNaoFinalizados = formProps.values.somenteNaoFinalizados

    return (
      <Box style={{ width: '42rem', marginTop: '1rem', marginRight: '-0.1875rem' }}>
        <FormDebouncedValueSpy onChange={(formState) => onChangeFilter(formState.values)} loadOnMount={false} />
        <Grid gapVertical={1} gap={1} justifyContent='flex-end'>
          <Cell size={12}>
            <Grid gapVertical={0.5}>
              <Cell size={12}>
                <Heading level={5}>Status do atendimento</Heading>
              </Cell>
              <Cell size={12} style={{ marginLeft: '-0.25rem' }}>
                <Grid gap={0} gapVertical={0.5}>
                  <Cell size={4}>
                    <CheckboxField
                      name={path.statusAtendimento}
                      label={filterLabelRender('Aguardando atendimento', statusAtendStyles.aguardandoAtendimentoBox)}
                      value={StatusAtendimento.AGUARDANDO_ATENDIMENTO}
                    />
                  </Cell>
                  <Cell size={4}>
                    <CheckboxField
                      name={path.statusAtendimento}
                      label={filterLabelRender('Em atendimento', statusAtendStyles.emAtendimentoBox)}
                      value={StatusAtendimento.EM_ATENDIMENTO}
                    />
                  </Cell>
                  <Cell size={4}>
                    <CheckboxField
                      name={path.statusAtendimento}
                      label={filterLabelRender('Em escuta inicial', statusAtendStyles.emEscutaInicialBox)}
                      value={StatusAtendimento.EM_ESCUTA_INICIAL}
                    />
                  </Cell>
                  <Cell size={4}>
                    <CheckboxField
                      name={path.statusAtendimento}
                      label={filterLabelRender('Atendimento realizado', statusAtendStyles.atendimentoRealizadoBox)}
                      value={StatusAtendimento.ATENDIMENTO_REALIZADO}
                      disabled={isSomenteNaoFinalizados}
                    />
                  </Cell>
                  <Cell size={4}>
                    <CheckboxField
                      name={path.statusAtendimento}
                      label={filterLabelRender('Não aguardou', statusAtendStyles.naoAguardouBox)}
                      value={StatusAtendimento.NAO_AGUARDOU}
                      disabled={isSomenteNaoFinalizados}
                    />
                  </Cell>
                </Grid>
              </Cell>
            </Grid>
          </Cell>
          <Cell size={12}>
            <SwitchField name={path.somenteNaoFinalizados} label='Ver somente os atendimentos não finalizados' />
          </Cell>
          <Cell>
            <Grid gapVertical={1}>
              <Cell size={6}>
                <HFlow alignItems='flex-end'>
                  <DateRangeField
                    name={path.periodo}
                    label='Período'
                    maxDate={new Date()}
                    disabled={isSomenteNaoFinalizados}
                  />
                </HFlow>
              </Cell>
              <Cell size={6}>
                <TipoServicoSelectField label='Tipo serviço' name={path.tiposServico} multiple includeInterno={true} />
              </Cell>
              <Cell size={6}>
                <EquipeSelectField label='Equipe' name={path.equipes} unidadeSaudeId={unidadeSaude.id} multiple />
              </Cell>
              <Cell size={6}>
                <LotacaoSelectField
                  label='Profissional'
                  name={path.lotacoes}
                  unidadeSaudeId={unidadeSaude.id}
                  multiple
                />
              </Cell>
            </Grid>
          </Cell>
          <Cell alignSelf='flex-end'>
            <Button onClick={onFechar} size='small' skin='outline' kind='normal'>
              Fechar
            </Button>
          </Cell>

          <Cell alignSelf='flex-end'>
            <Tooltip text='Redefinir filtros para o padrão'>
              <Button kind='normal' size='small' onClick={onClear} disabled={isFilterEqual}>
                <Icon style={{ paddingRight: '0.5rem' }} icon='redo' />
                Voltar para padrão
              </Button>
            </Tooltip>
          </Cell>

          <Cell alignSelf='flex-end'>
            <SubmitButton size='small' handleSubmit={formProps.handleSubmit}>
              Filtrar
            </SubmitButton>
          </Cell>
        </Grid>
      </Box>
    )
  }
  return (
    <Form<ListaAtendimentoFilterPopperModel>
      render={renderForm}
      onSubmit={onSubmit}
      initialValues={initialValues}
      onSubmitSucceeded={onFechar}
      decorators={decorator}
    />
  )
}

const filterLabelRender = (nome: string, css: CSSProperties) => {
  return (
    <HFlow hSpacing={0} justifyContent='center' alignItems='center'>
      <div
        style={{
          ...css,
          width: '0.5rem',
          height: '1.5rem',
          borderRadius: '0.125rem',
          marginRight: '0.5rem',
        }}
      />
      {nome}
    </HFlow>
  )
}
